/**
 * @fileoverview Reusable multi-select combobox built on shadcn Command + Popover.
 * @module shared/components/filters/MultiCombobox
 */

import { useState, useEffect, useCallback, useRef, type ReactElement, type ReactNode } from 'react'
import { ChevronsUpDown, CheckSquare, Square } from 'lucide-react'
import { Button } from '@/shared/components/ui'
import { Kbd, KbdGroup } from '@/components/ui/kbd'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'

/**
 * Generic option used by the combobox.
 */
export interface ComboboxOption {
  value: string
  text: string
  additionalText?: string
}

interface MultiComboboxProps {
  options: ComboboxOption[]
  selectedValues: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  disabled?: boolean
  isLoading?: boolean
  /** Additional className for outer container */
  className?: string
  /** Show Clear button when items are selected @default true */
  showClearButton?: boolean
  /**
   * Compact icon-only trigger. When set, renders a square icon button with a
   * selection-count badge instead of the full-width labelled trigger.
   */
  triggerIcon?: ReactNode
}

/**
 * Multi-select combobox using ShadCN Command for accessible search/filter UX.
 *
 * @component
 */
export function MultiCombobox({
  options,
  selectedValues,
  onChange,
  placeholder = 'Search...',
  disabled = false,
  isLoading = false,
  className = '',
  showClearButton = true,
  triggerIcon,
}: MultiComboboxProps): ReactElement {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const toggle = useCallback((value: string): void => {
    const next = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value]
    onChange(next)
  }, [selectedValues, onChange])

  const handleClear = useCallback((): void => {
    onChange([])
    setOpen(false)
  }, [onChange])

  // Keyboard shortcut: Esc clears all selections even while the popover is
  // closed. The open case is handled by PopoverContent's onEscapeKeyDown
  // (Radix consumes Esc in the capture phase, so a document listener would
  // race with it). Esc from another editable element is left alone — there it
  // means "clear/leave that input", not "clear these filters".
  useEffect(() => {
    if (open || selectedValues.length === 0) return

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      const target = e.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return
      }
      handleClear()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, selectedValues.length, handleClear])

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen)
  }, [])

  return (
    <div ref={containerRef} className={`${className}`}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          {triggerIcon ? (
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-label={placeholder}
              className="glass-button relative size-9 p-0 text-muted-foreground"
              disabled={disabled || isLoading}
            >
              {triggerIcon}
              {selectedValues.length > 0 && (
                <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-semibold leading-none text-primary-foreground">
                  {selectedValues.length}
                </span>
              )}
            </Button>
          ) : (
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-label={placeholder}
              className="glass-button px-4 py-2 w-full justify-between"
              disabled={disabled || isLoading}
            >
              <span className="text-muted-foreground">{isLoading ? 'Loading...' : placeholder}</span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 text-muted-foreground" />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="w-[300px] p-0"
          align="start"
          onEscapeKeyDown={() => {
            // Esc clears the selection; the popover closes via Radix's default.
            if (selectedValues.length > 0) onChange([])
          }}
        >
          <Command>
            <CommandInput placeholder={placeholder} />
            <CommandEmpty>No options found.</CommandEmpty>
            <CommandList className="max-h-[32rem] overflow-y-auto scrollbar-elegant">
              <CommandGroup>
                {options.map(option => {
                  const active = selectedValues.includes(option.value)
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => toggle(option.value)}
                      className="flex items-center gap-2"
                    >
                      {active ? (
                        <CheckSquare className="mr-2 h-4 w-4 text-accent-foreground" />
                      ) : (
                        <Square className="mr-2 h-4 w-4 opacity-40" />
                      )}
                      <span className="flex-1">{option.text}</span>
                      {option.additionalText && (
                        <span className="ml-auto text-sm text-muted-foreground">
                          {option.additionalText}
                        </span>
                      )}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
          {showClearButton && selectedValues.length > 0 && (
            <div className="border-t border-border/30 p-1.5">
              <Button
                variant="ghost"
                onClick={handleClear}
                aria-keyshortcuts="Escape"
                className="h-8 w-full justify-start gap-2 px-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/20 group"
              >
                Clear
                <KbdGroup className="ml-auto">
                  <Kbd>Esc</Kbd>
                </KbdGroup>
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
